import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { API_BASE_URL } from '../../config';
import PaymentProcessingModal from './PaymentProcessingModal';
import './PortfolioDashboard.css';

const stripePromise = loadStripe('pk_test_51Px9X7GvXm129m7qC7z9z9z9z9z9z9z9'); 


const InvoicesPaymentsScreen = ({ dateRange: propDateRange, propertyId: propPropertyId }) => {
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState('payments'); 
  const [dateRange, setDateRange] = useState(propDateRange || {
    // Default to current month and next 11 months to show scheduled payments
    start_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end_date: new Date(new Date().getFullYear(), new Date().getMonth() + 12, 0).toISOString().split('T')[0]
  });
  const [filters, setFilters] = useState({
    property_id: propPropertyId || null,
    tenant_id: null,
    status: 'all',
    payment_method: 'all'
  });
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [properties, setProperties] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPaymentForStripe, setSelectedPaymentForStripe] = useState(null);
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [availableInvoices, setAvailableInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [newPayment, setNewPayment] = useState({
    invoice_id: '',
    tenant_id: '',
    property_id: '',
    amount: '',
    due_date: new Date().toISOString().split('T')[0],
    paid_date: '',
    status: 'pending',
    payment_method: 'Stripe',
    reference_number: ''
  });
  const [showDocumentUploadModal, setShowDocumentUploadModal] = useState(false);
  const [selectedPaymentForDocument, setSelectedPaymentForDocument] = useState(null);

  // Sync props to state if they change
  useEffect(() => {
    if (propDateRange) setDateRange(propDateRange);
    if (propPropertyId !== undefined) setFilters(prev => ({ ...prev, property_id: propPropertyId }));
  }, [propDateRange, propPropertyId]);

  useEffect(() => {
    fetchProperties();
    fetchTenants();
    fetchData();
  }, [activeView, dateRange, filters]);

  const fetchProperties = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/properties`);
      if (response.ok) {
        const data = await response.json();
        setProperties(data);
      }
    } catch (err) {
      console.error('Error fetching properties:', err);
    }
  };

  const fetchTenants = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/tenants`);
      if (response.ok) {
        const data = await response.json();
        setTenants(data);
      }
    } catch (err) {
      console.error('Error fetching tenants:', err);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('start_date', dateRange.start_date);
      params.append('end_date', dateRange.end_date);
      if (filters.property_id) params.append('property_id', filters.property_id);
      if (filters.tenant_id) params.append('tenant_id', filters.tenant_id);
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.payment_method !== 'all') params.append('payment_method', filters.payment_method);

      if (activeView === 'invoices') {
        const response = await fetch(`${API_BASE_URL}/api/invoices?${params}`);
        if (response.ok) {
          const data = await response.json();
          setInvoices(data);
        }
      } else if (activeView === 'payments') {
        const response = await fetch(`${API_BASE_URL}/api/payments?${params}`);
        if (response.ok) {
          const data = await response.json();
          setPayments(data);
        }
      } else if (activeView === 'payouts') {
        const response = await fetch(`${API_BASE_URL}/api/payouts?${params}`);
        if (response.ok) {
          const data = await response.json();
          setPayouts(data);
        }
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const getStatusBadge = (status) => {
    const colors = {
      'paid': { bg: '#d4edda', color: '#155724' },
      'overdue': { bg: '#f8d7da', color: '#721c24' },
      'open': { bg: '#fff3cd', color: '#856404' },
      'failed': { bg: '#f8d7da', color: '#721c24' },
      'pending': { bg: '#d1ecf1', color: '#0c5460' },
      'scheduled': { bg: '#e7f3ff', color: '#004085' },
      'succeeded': { bg: '#d4edda', color: '#155724' },
      'refunded': { bg: '#e2e3e5', color: '#383d41' },
      'disputed': { bg: '#f8d7da', color: '#721c24' }
    };
    const style = colors[status] || { bg: '#e2e8f0', color: '#475569' };
    return (
      <span style={{
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 600,
        backgroundColor: style.bg,
        color: style.color
      }}>
        {status}
      </span>
    );
  };

  const handleRecordClick = (record) => {
    // If it's an unpaid payment, open payment modal
    if (activeView === 'payments' && record.status !== 'paid' && record.status !== 'succeeded') {
      setSelectedPaymentForStripe(record);
      setShowPaymentModal(true);
    } else {
      setSelectedRecord(record);
      setShowDetailModal(true);
    }
  };

  const fetchAvailableInvoices = async (tenantId) => {
    if (!tenantId) {
      setAvailableInvoices([]);
      return;
    }
    try {
      setLoadingInvoices(true);
      // Fetch all invoices and filter for open/partial status on frontend
      const response = await fetch(`${API_BASE_URL}/api/invoices?tenant_id=${tenantId}`);
      if (response.ok) {
        const data = await response.json();
        // Filter to only show invoices with outstanding balance and open/partial status
        const openInvoices = data.filter(inv => {
          const outstanding = inv.amount - (inv.paid_amount || 0);
          return outstanding > 0 && (inv.status === 'open' || inv.status === 'partial');
        });
        setAvailableInvoices(openInvoices);
      } else {
        setAvailableInvoices([]);
      }
    } catch (err) {
      console.error('Error fetching invoices:', err);
      setAvailableInvoices([]);
    } finally {
      setLoadingInvoices(false);
    }
  };

  const handleInvoiceChange = (invoiceId) => {
    const selectedInvoice = availableInvoices.find(inv => inv.id === parseInt(invoiceId));
    if (selectedInvoice) {
      const outstandingAmount = selectedInvoice.amount - (selectedInvoice.paid_amount || 0);
      setNewPayment({
        ...newPayment,
        invoice_id: invoiceId,
        amount: outstandingAmount.toFixed(2),
        due_date: selectedInvoice.due_date || newPayment.due_date,
        property_id: selectedInvoice.property_id || newPayment.property_id,
        tenant_id: selectedInvoice.tenant_id || newPayment.tenant_id
      });
    } else {
      setNewPayment({
        ...newPayment,
        invoice_id: invoiceId,
        amount: ''
      });
    }
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    
    if (!newPayment.invoice_id) {
      alert('Please select an invoice first');
      return;
    }

    try {
      // First create the payment record with pending status
      const paymentResponse = await fetch(`${API_BASE_URL}/api/payments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newPayment,
          invoice_id: parseInt(newPayment.invoice_id),
          status: 'pending'
        })
      });

      if (!paymentResponse.ok) {
        const errorData = await paymentResponse.json();
        throw new Error(errorData.error || 'Failed to create payment record');
      }

      const paymentData = await paymentResponse.json();
      const paymentId = paymentData.payment_id || paymentData.id;

      // Now create Stripe checkout session
      const checkoutResponse = await fetch(`${API_BASE_URL}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payment_id: paymentId,
          amount: parseFloat(newPayment.amount) * 100, // Convert to cents
          success_url: `${window.location.origin}/checkout/${paymentId}/success?return=/financial`,
          cancel_url: `${window.location.origin}/financial`
        })
      });

      if (!checkoutResponse.ok) {
        const errorData = await checkoutResponse.json();
        throw new Error(errorData.error || 'Failed to create Stripe checkout session');
      }

      const checkoutData = await checkoutResponse.json();
      
      // Redirect to Stripe checkout
      if (checkoutData.url) {
        window.location.href = checkoutData.url;
      } else {
        throw new Error('No checkout URL received from Stripe');
      }

      // Reset form
      setShowAddPaymentModal(false);
      setNewPayment({
        invoice_id: '',
        tenant_id: '',
        property_id: '',
        amount: '',
        due_date: new Date().toISOString().split('T')[0],
        paid_date: '',
        status: 'pending',
        payment_method: 'Stripe',
        reference_number: ''
      });
      setAvailableInvoices([]);
    } catch (err) {
      console.error('Error processing payment:', err);
      alert(`Error processing payment: ${err.message}`);
    }
  };

  const exportToCSV = () => {
    let data = [];
    let filename = '';
    
    if (activeView === 'invoices') {
      data = invoices;
      filename = 'invoices.csv';
    } else if (activeView === 'payments') {
      data = payments;
      filename = 'payments.csv';
    } else if (activeView === 'payouts') {
      data = payouts;
      filename = 'payouts.csv';
    }

    if (data.length === 0) return;

    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => headers.map(header => {
        const value = row[header];
        return typeof value === 'string' && value.includes(',') ? `"${value}"` : value;
      }).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="invoices-payments-screen">
      {/* Header */}
      <div className="screen-header">
        <h1>Invoices & Payments</h1>
        <div className="header-actions">
          <button 
            onClick={() => setShowAddPaymentModal(true)} 
            className="export-btn"
            style={{ backgroundColor: '#10b981' }}
          >
            + Add Payment
          </button>
          <button onClick={exportToCSV} className="export-btn">Export CSV</button>
        </div>
      </div>

      {/* View Toggle */}
      <div className="view-tabs">
        <button
          className={activeView === 'payments' ? 'active' : ''}
          onClick={() => setActiveView('payments')}
        >
          Rental Payments
        </button>
        <button
          className={activeView === 'invoices' ? 'active' : ''}
          onClick={() => setActiveView('invoices')}
        >
          Invoices
        </button>
        <button
          className={activeView === 'payouts' ? 'active' : ''}
          onClick={() => setActiveView('payouts')}
        >
          Payouts
        </button>
      </div>

      {/* Local Filters (Simplified) */}
      <div className="filters-section">
        <div className="filter-group">
          <label>Status:</label>
          <select
            value={filters.status}
            onChange={(e) => setFilters({...filters, status: e.target.value})}
            className="filter-select"
          >
            <option value="all">All Statuses</option>
            <option value="paid">Paid</option>
            <option value="overdue">Overdue</option>
            <option value="open">Open</option>
            <option value="failed">Failed</option>
            <option value="pending">Pending</option>
            <option value="scheduled">Scheduled</option>
          </select>
        </div>
        {activeView === 'payments' && (
          <div className="filter-group">
            <label>Method:</label>
            <select
              value={filters.payment_method}
              onChange={(e) => setFilters({...filters, payment_method: e.target.value})}
              className="filter-select"
            >
              <option value="all">All Methods</option>
              <option value="card">Card</option>
              <option value="ach">ACH</option>
              <option value="cash">Cash</option>
              <option value="check">Check</option>
            </select>
          </div>
        )}
      </div>

      {/* Data Table */}
      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      ) : (
        <div className="table-container">
          {activeView === 'invoices' && (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Tenant</th>
                  <th>Property</th>
                  <th>Unit</th>
                  <th>Period</th>
                  <th>Due Date</th>
                  <th>Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {invoices.length === 0 ? (
                  <tr><td colSpan="8" className="empty-state">No invoices found</td></tr>
                ) : (
                  invoices.map(inv => (
                    <tr key={inv.id} onClick={() => handleRecordClick(inv)} className="clickable-row">
                      <td className="bold">{inv.invoice_number || `INV-${inv.id}`}</td>
                      <td>{inv.tenant_name || '-'}</td>
                      <td>{inv.property_name || '-'}</td>
                      <td>{inv.unit_number || '-'}</td>
                      <td className="date-cell">{formatDate(inv.period_start)} - {formatDate(inv.period_end)}</td>
                      <td className="date-cell">{formatDate(inv.due_date)}</td>
                      <td className="bold">{formatCurrency(inv.amount)}</td>
                      <td>
                        <span className={`status-badge ${inv.status}`}>{inv.status}</span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}

          {activeView === 'payments' && (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Tenant</th>
                  <th>Property</th>
                  <th>Amount</th>
                  <th>Method</th>
                  <th>Status</th>
                  <th>Reference</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {payments.length === 0 ? (
                  <tr><td colSpan="8" className="empty-state">No payments found</td></tr>
                ) : (
                  payments.map(payment => (
                    <tr 
                      key={payment.id} 
                      onClick={() => handleRecordClick(payment)} 
                      className={payment.status !== 'paid' && payment.status !== 'succeeded' ? 'clickable-row highlight-row' : 'clickable-row'}
                    >
                      <td className="date-cell">
                        {payment.paid_date 
                          ? formatDate(payment.paid_date) 
                          : payment.due_date 
                            ? `Due: ${formatDate(payment.due_date)}` 
                            : formatDate(payment.created_at)}
                      </td>
                      <td className="bold">{payment.tenant_name || '-'}</td>
                      <td>{payment.property_name || '-'}</td>
                      <td className="bold">{formatCurrency(payment.amount)}</td>
                      <td>{payment.payment_method || '-'}</td>
                      <td>
                        <span className={`status-badge ${payment.status}`}>{payment.status}</span>
                      </td>
                      <td className="text-cell">
                        {payment.stripe_charge_id ? (
                          <a 
                            href={`https://dashboard.stripe.com/payments/${payment.stripe_charge_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="stripe-link"
                          >
                            Stripe Ref →
                          </a>
                        ) : payment.reference_number || '-'}
                      </td>
                      <td>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedPaymentForDocument(payment);
                            setShowDocumentUploadModal(true);
                          }}
                          className="small-action-btn"
                          title="Upload receipt"
                        >
                          📎
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}

          {activeView === 'payouts' && (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Arrival Date</th>
                  <th>Stripe Payout ID</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                {payouts.length === 0 ? (
                  <tr><td colSpan="5" className="empty-state">No payouts found</td></tr>
                ) : (
                  payouts.map(payout => (
                    <tr key={payout.id} onClick={() => handleRecordClick(payout)} className="clickable-row">
                      <td className="date-cell">{formatDate(payout.arrival_date || payout.created_at)}</td>
                      <td>
                        <a 
                          href={`https://dashboard.stripe.com/payouts/${payout.stripe_payout_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="stripe-link"
                        >
                          {payout.stripe_payout_id}
                        </a>
                      </td>
                      <td className="bold">{formatCurrency(payout.amount)}</td>
                      <td>
                        <span className={`status-badge ${payout.status}`}>{payout.status}</span>
                      </td>
                      <td>{payout.description || '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Payment Processing Modal */}
      {showPaymentModal && selectedPaymentForStripe && (
        <Elements stripe={stripePromise}>
          <PaymentProcessingModal
            payment={selectedPaymentForStripe}
            onClose={() => {
              setShowPaymentModal(false);
              setSelectedPaymentForStripe(null);
            }}
            onSuccess={() => {
              setShowPaymentModal(false);
              setSelectedPaymentForStripe(null);
              fetchData();
            }}
          />
        </Elements>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedRecord && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
            <div className="modal-header">
              <h2>
                {activeView === 'invoices' ? 'Invoice Details' : 
                 activeView === 'payments' ? 'Payment Details' : 'Payout Details'}
              </h2>
              <button className="modal-close" onClick={() => setShowDetailModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="detail-grid">
                {Object.entries(selectedRecord).map(([key, value]) => {
                  if (typeof value === 'object' && value !== null) return null;
                  return (
                    <div key={key} className="detail-item">
                      <label>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</label>
                      <span>{String(value || '-')}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Document Upload Modal */}
      {showDocumentUploadModal && selectedPaymentForDocument && (
        <div className="modal-overlay" onClick={() => setShowDocumentUploadModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>Upload Payment Receipt</h2>
              <button className="modal-close" onClick={() => setShowDocumentUploadModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <PaymentDocumentUploadForm
                payment={selectedPaymentForDocument}
                onUploadComplete={() => {
                  setShowDocumentUploadModal(false);
                  setSelectedPaymentForDocument(null);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Add Payment Modal */}
      {showAddPaymentModal && (
        <div className="modal-overlay" onClick={() => setShowAddPaymentModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2>Add Rental Payment</h2>
              <button className="modal-close" onClick={() => setShowAddPaymentModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleAddPayment} className="form-grid">
                <div className="form-field">
                  <label>Tenant *</label>
                  <select
                    value={newPayment.tenant_id}
                    onChange={(e) => {
                      const tenantId = e.target.value;
                      setNewPayment({...newPayment, tenant_id: tenantId, invoice_id: '', amount: ''});
                      fetchAvailableInvoices(tenantId);
                    }}
                    className="form-input"
                    required
                  >
                    <option value="">Select Tenant</option>
                    {tenants.map(t => (
                      <option key={t.id} value={t.id}>
                        {t.first_name || t['First Name']} {t.last_name || t['Last Name']}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-field">
                  <label>Invoice *</label>
                  <select
                    value={newPayment.invoice_id}
                    onChange={(e) => handleInvoiceChange(e.target.value)}
                    className="form-input"
                    required
                    disabled={!newPayment.tenant_id || loadingInvoices}
                  >
                    <option value="">
                      {loadingInvoices 
                        ? 'Loading invoices...' 
                        : !newPayment.tenant_id 
                          ? 'Select tenant first'
                          : availableInvoices.length === 0
                            ? 'No open invoices found'
                            : 'Select Invoice'}
                    </option>
                    {availableInvoices.map(inv => {
                      const outstanding = inv.amount - (inv.paid_amount || 0);
                      return (
                        <option key={inv.id} value={inv.id}>
                          {inv.invoice_number || `INV-${inv.id}`} - 
                          Period: {new Date(inv.period_start).toLocaleDateString()} to {new Date(inv.period_end).toLocaleDateString()} - 
                          Due: ${outstanding.toFixed(2)} of ${inv.amount.toFixed(2)}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div className="form-field">
                  <label>Amount Owed ($) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newPayment.amount}
                    onChange={(e) => setNewPayment({...newPayment, amount: e.target.value})}
                    className="form-input"
                    required
                    disabled={!newPayment.invoice_id}
                    title={newPayment.invoice_id ? "Amount is auto-populated from invoice. You can adjust if paying partial amount." : "Select an invoice first"}
                  />
                </div>
                <div className="form-field">
                  <label>Due Date *</label>
                  <input
                    type="date"
                    value={newPayment.due_date}
                    onChange={(e) => setNewPayment({...newPayment, due_date: e.target.value})}
                    className="form-input"
                    required
                  />
                </div>
                <div className="form-field">
                  <label>Payment Method</label>
                  <select
                    value={newPayment.payment_method}
                    onChange={(e) => setNewPayment({...newPayment, payment_method: e.target.value})}
                    className="form-input"
                  >
                    <option value="Stripe">Stripe (Card/ACH)</option>
                    <option value="Cash">Cash</option>
                    <option value="Check">Check</option>
                    <option value="Bank Transfer">Bank Transfer</option>
                  </select>
                  {newPayment.payment_method === 'Stripe' && (
                    <small style={{ color: '#6b7280', marginTop: '4px', display: 'block' }}>
                      You will be redirected to Stripe checkout to complete payment
                    </small>
                  )}
                </div>
                <div className="form-field">
                  <label>Reference #</label>
                  <input
                    type="text"
                    value={newPayment.reference_number}
                    onChange={(e) => setNewPayment({...newPayment, reference_number: e.target.value})}
                    className="form-input"
                    placeholder="e.g. Check #"
                  />
                </div>
                <div className="modal-actions" style={{ gridColumn: 'span 2' }}>
                  <button 
                    type="submit" 
                    className="save-button"
                    disabled={!newPayment.invoice_id || !newPayment.amount || newPayment.amount <= 0}
                  >
                    {newPayment.payment_method === 'Stripe' ? 'Continue to Stripe Checkout' : 'Record Payment'}
                  </button>
                  <button type="button" className="cancel-button" onClick={() => {
                    setShowAddPaymentModal(false);
                    setNewPayment({
                      invoice_id: '',
                      tenant_id: '',
                      property_id: '',
                      amount: '',
                      due_date: new Date().toISOString().split('T')[0],
                      paid_date: '',
                      status: 'pending',
                      payment_method: 'Stripe',
                      reference_number: ''
                    });
                    setAvailableInvoices([]);
                  }}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const PaymentDocumentUploadForm = ({ payment, onUploadComplete }) => {
  const [file, setFile] = useState(null);
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file || !payment.tenant_id) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('description', description || `Receipt for payment ${payment.id}`);
      formData.append('document_type', 'payment_receipt');
      formData.append('category', 'financial');
      if (payment.id) formData.append('payment_id', payment.id);

      const response = await fetch(`${API_BASE_URL}/api/financial/tenants/${payment.tenant_id}/documents`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        onUploadComplete();
      } else {
        alert('Error uploading document');
      }
    } catch (err) {
      console.error('Error uploading:', err);
      alert('Error uploading document');
    } finally {
      setUploading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="document-upload-form">
      <div className="form-field">
        <label>Payment Details</label>
        <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '6px', fontSize: '14px' }}>
          <div>Amount: ${payment.amount}</div>
          <div>Date: {new Date(payment.paid_date || payment.created_at).toLocaleDateString()}</div>
        </div>
      </div>
      <div className="form-field">
        <label>Receipt File</label>
        <input
          type="file"
          onChange={(e) => setFile(e.target.files[0])}
          className="form-input"
          required
          accept=".pdf,.jpg,.jpeg,.png"
        />
      </div>
      <div className="form-field">
        <label>Description</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="form-input"
          rows="3"
          placeholder="Optional description"
        />
      </div>
      <div className="modal-actions">
        <button type="submit" className="save-button" disabled={uploading}>
          {uploading ? 'Uploading...' : 'Upload Receipt'}
        </button>
        <button type="button" className="cancel-button" onClick={() => onUploadComplete()}>
          Cancel
        </button>
      </div>
    </form>
  );
};

export default InvoicesPaymentsScreen;

